import React, { useEffect, useRef, useState } from "react";
import ChoixChips from "../../common/ChoixChips/ChoixChips";
import ModaleLocale from "../../common/CustomModal/ModaleLocale";
import { Pill } from "../../common/DataList";
import {
  SITUATIONS,
  TERRITOIRES,
  basculerCodePostal,
  basculerCommune,
  basculerTerritoire,
  compteTerritoire,
  corpsRequete,
  enClair,
  erreursAffichees,
  etatCommune,
  jourFr,
  saisieDepuisRegle,
  saisieVide,
  validerSaisie,
} from "./reglesSousOrientation";
import { ajouterRegle, modifierRegle, supprimerRegle } from "./reglesSousOrientationApi";

// ============================================================
// Saisie d'une règle (CE-1419, maquette 11) : « En clair » en tête, puis
// Qui ? Où ? Vers qui ?, et la position dans la liste. Une commune à
// plusieurs codes postaux a une case par code, sa propre case passe à l'état
// mixte. Les erreurs s'affichent sous leur champ, celles de l'écran comme
// celles de l'API (mêmes clés) ; un refus 409 en bandeau. Supprimer demande
// confirmation dans le pied de la fenêtre.
// ============================================================

const SEXES = [
  { id: "", name: "Tous" },
  { id: "FEMME", name: "Femmes" },
  { id: "HOMME", name: "Hommes" },
];
const RSA = [
  { id: "", name: "Tous" },
  { id: "oui", name: "Oui" },
  { id: "non", name: "Non" },
];
const GROUPES_SITUATIONS = [
  { titre: "Personne seule", seule: true },
  { titre: "En couple", seule: false },
];
const ORDRE_CHAMPS = ["age", "situationsFamiliales", "communes", "operateurs"];

/** « ST GILLES LES BAINS » → « St Gilles Les Bains » : les lieux-dits de La Poste, en majuscules dans `cities`. */
const casseLieu = (lieu) => lieu.toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_, separateur, lettre) => separateur + lettre.toUpperCase());

/** Une case qui peut être mixte : l'état `indeterminate` ne se pose qu'en JavaScript. */
const CaseMixte = ({ etat, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = etat === "partiel";
  }, [etat]);
  return <input ref={ref} type="checkbox" checked={etat !== "vide"} {...props} />;
};

const Erreur = ({ id, message }) =>
  message ? (
    <p className="saisie-regle__erreur" id={id} role="alert">
      {message}
    </p>
  ) : null;

const ModaleRegle = ({ version, regle, referentiel, onFermer, onEnregistree }) => {
  const nombre = version.regles.length + (regle ? 0 : 1);
  const [saisie, setSaisie] = useState(() => ({
    ...(regle ? saisieDepuisRegle(regle, referentiel) : saisieVide(referentiel)),
    rang: regle ? regle.rang : nombre,
  }));
  // Les erreurs de l'écran s'effacent dès que leur champ est corrigé ; celles de l'API attendent le prochain envoi.
  const [erreursEcran, setErreursEcran] = useState({});
  const [erreursApi, setErreursApi] = useState({});
  const [refus, setRefus] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  // Les territoires dépliés : ceux qui ont une commune à l'ouverture, puis au gré de l'utilisateur.
  const [ouverts, setOuverts] = useState(() => new Set(referentiel.territoires.filter((t) => compteTerritoire(saisie, t).cochees > 0).map((t) => t.id)));
  const refGarder = useRef(null);
  const refSupprimer = useRef(null);
  const enConfirmation = useRef(false);

  // La confirmation remplace le pied : le focus va à « Garder la règle », puis revient à « Supprimer la règle ».
  useEffect(() => {
    if (confirmation && refGarder.current) refGarder.current.focus();
    if (!confirmation && enConfirmation.current && refSupprimer.current) refSupprimer.current.focus();
    enConfirmation.current = confirmation;
  }, [confirmation]);

  const changer = (champ, valeur) => setSaisie((s) => ({ ...s, [champ]: valeur }));
  const erreursVisibles = erreursAffichees(erreursEcran, erreursApi, saisie);
  const basculer = (liste, valeur, coche) => (coche ? [...liste, valeur] : liste.filter((v) => v !== valeur));
  // `coche` est lu au moment de l'événement : dans la mise à jour, React aurait déjà remis la case à sa valeur contrôlée.
  const basculerAvec = (bascule, cible, coche) => setSaisie((actuelle) => bascule(actuelle, cible, coche));

  const deplier = (id, ouvert) =>
    setOuverts((actuels) => {
      if (actuels.has(id) === ouvert) return actuels;
      const suivants = new Set(actuels);
      if (ouvert) suivants.add(id);
      else suivants.delete(id);
      return suivants;
    });

  const focaliserErreur = (lues) => {
    const premier = ORDRE_CHAMPS.find((champ) => lues[champ]);
    if (premier === "communes" && referentiel.territoires.length) deplier(referentiel.territoires[0].id, true);
    // Après le rendu : un territoire replié vient d'être déplié pour montrer l'erreur des communes.
    window.setTimeout(() => {
      const cible = premier && document.querySelector(`[data-champ-regle="${premier}"]`);
      if (cible) cible.focus();
    }, 0);
  };

  const echec = (e) => {
    setEnCours(false);
    if (e.champs) {
      const { criteres, rang, ...parChamp } = e.champs;
      setErreursApi(parChamp);
      setRefus(criteres || rang || null);
      focaliserErreur(parChamp);
    } else {
      setRefus(e.message);
    }
  };

  const enregistrer = (evenement) => {
    evenement.preventDefault();
    const lues = validerSaisie(saisie);
    setErreursEcran(lues);
    setErreursApi({});
    setRefus(null);
    if (Object.keys(lues).length) {
      focaliserErreur(lues);
      return;
    }
    setEnCours(true);
    const corps = corpsRequete(saisie, referentiel);
    const appel = regle ? modifierRegle(version.id, regle.id, corps) : ajouterRegle(version.id, corps);
    appel.then((relue) => {
      const quand = version.statut.id === "A_VENIR" ? `Elle s'appliquera à partir du ${jourFr(version.enVigueurLe)}.` : "Elle vaut pour les prochaines orientations.";
      onEnregistree(relue, `Règle ${saisie.rang} enregistrée. ${quand}`, { supprimee: false });
    }, echec);
  };

  const supprimer = () => {
    setEnCours(true);
    supprimerRegle(version.id, regle.id).then((relue) => onEnregistree(relue, "Règle supprimée.", { supprimee: true }), (e) => {
      setConfirmation(false);
      echec(e);
    });
  };

  const operateurs = referentiel.operateurs;
  // Un opérateur déjà retenu par la règle mais devenu inéligible reste affiché, pour pouvoir le retirer.
  const horsReferentiel = regle ? regle.operateurs.filter((o) => !operateurs.some((x) => x.id === o.id)) : [];

  const pied = confirmation ? (
    <>
      <p className="saisie-regle__confirmation" role="alert">
        Supprimer cette règle ? Les allocataires déjà sous-orientés gardent leur sous-orientation.
      </p>
      <button type="button" ref={refGarder} className="btn btn-ghost" onClick={() => setConfirmation(false)} disabled={enCours}>
        Garder la règle
      </button>
      <button type="button" className="btn btn-danger" onClick={supprimer} disabled={enCours}>
        Supprimer
      </button>
    </>
  ) : (
    <>
      {regle && (
        <button type="button" ref={refSupprimer} className="btn btn-ghost saisie-regle__supprimer" onClick={() => setConfirmation(true)} disabled={enCours}>
          Supprimer la règle
        </button>
      )}
      <button type="button" className="btn btn-ghost" onClick={onFermer} disabled={enCours}>
        Annuler
      </button>
      <button type="submit" form="formulaire-regle" className="btn btn-primary" disabled={enCours}>
        Enregistrer la règle
      </button>
    </>
  );

  return (
    <ModaleLocale
      ouvert
      onFermer={onFermer}
      titre={regle ? `Règle ${regle.rang}` : "Nouvelle règle"}
      sousTitre={version.statut.id === "A_VENIR" ? `Version à venir au ${jourFr(version.enVigueurLe)}` : `Version en vigueur depuis le ${jourFr(version.enVigueurLe)}`}
      taille="moyenne"
      idTitre="modale-regle"
      peutFermer={!enCours}
      pied={<div className={`saisie-regle__pied${regle ? " saisie-regle__pied--suppression" : ""}`}>{pied}</div>}
    >
      <form id="formulaire-regle" className="saisie-regle" onSubmit={enregistrer} noValidate>
        <p className="regles-encadre regles-encadre--info" aria-live="polite">
          <i className="fa fa-info-circle" aria-hidden="true" />
          <span>
            <b>En clair :</b> {enClair(saisie, referentiel)}
          </span>
        </p>
        {refus && (
          <div className="alert alert-danger" role="alert">
            {refus}
          </div>
        )}

        <section aria-labelledby="regle-qui">
          <h3 id="regle-qui">Qui ?</h3>
          <div className="saisie-regle__ligne">
            <ChoixChips name="regle-sexe" legend="Sexe" variant="segmente" options={SEXES} value={saisie.sexe} onChange={(v) => changer("sexe", v)} error={erreursVisibles.sexe} />
            <ChoixChips name="regle-rsa" legend="RSA majoré" variant="segmente" options={RSA} value={saisie.rsaMajore} onChange={(v) => changer("rsaMajore", v)} error={erreursVisibles.rsaMajore} />
            <div className="saisie-regle__champ">
              <label htmlFor="regle-age">Âge minimum dans six mois, facultatif</label>
              <div className="saisie-regle__unite">
                <input
                  id="regle-age"
                  className="form-control saisie-regle__age"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={saisie.ageMin}
                  onChange={(e) => changer("ageMin", e.target.value)}
                  aria-invalid={erreursVisibles.age ? "true" : undefined}
                  aria-describedby={`aide-age${erreursVisibles.age ? " erreur-age" : ""}`}
                  data-champ-regle="age"
                />
                <span>ans et plus</span>
              </div>
              <p className="saisie-regle__aide" id="aide-age">
                Comme l'algorithme d'orientation : l'âge que l'allocataire aura six mois après son orientation.
              </p>
              <Erreur id="erreur-age" message={erreursVisibles.age} />
            </div>
          </div>

          <fieldset className="saisie-regle__groupe" aria-describedby={erreursVisibles.situationsFamiliales ? "erreur-situations" : undefined}>
            <legend>
              Situation familiale <span aria-hidden="true">*</span>
            </legend>
            <div className="saisie-regle__situations">
              {GROUPES_SITUATIONS.map((groupe) => (
                <div key={groupe.titre} className="saisie-regle__situations-groupe" role="group" aria-label={groupe.titre}>
                  <p className="saisie-regle__sous-titre" aria-hidden="true">
                    {groupe.titre}
                  </p>
                  {SITUATIONS.filter((s) => Boolean(s.seule) === groupe.seule).map((s, i) => (
                    <label key={s.id} className="case-ligne">
                      <input
                        type="checkbox"
                        checked={saisie.situations.includes(s.id)}
                        onChange={(e) => changer("situations", basculer(saisie.situations, s.id, e.target.checked))}
                        data-champ-regle={groupe.seule && i === 0 ? "situationsFamiliales" : undefined}
                      />
                      <span>{s.nom}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <Erreur id="erreur-situations" message={erreursVisibles.situationsFamiliales} />
          </fieldset>
        </section>

        <section aria-labelledby="regle-ou">
          <h3 id="regle-ou">
            Où ? <span aria-hidden="true">*</span>
          </h3>
          <div role="group" aria-labelledby="regle-ou" aria-describedby={erreursVisibles.communes ? "erreur-communes" : undefined}>
          {referentiel.territoires.map((territoire, indexTerritoire) => {
            const compte = compteTerritoire(saisie, territoire);
            return (
              <details key={territoire.id} className="saisie-regle__territoire" open={ouverts.has(territoire.id)} onToggle={(e) => deplier(territoire.id, e.currentTarget.open)}>
                <summary>
                  <i className="fa fa-chevron-right" aria-hidden="true" />
                  {territoire.libelle}
                  <span className="saisie-regle__compteur">
                    {compte.cochees} / {compte.total}
                  </span>
                </summary>
                <div className="saisie-regle__territoire-corps">
                  <label className="case-ligne case-ligne--tout">
                    <input
                      type="checkbox"
                      checked={compte.entier}
                      onChange={(e) => basculerAvec(basculerTerritoire, territoire, e.target.checked)}
                      data-champ-regle={indexTerritoire === 0 ? "communes" : undefined}
                      aria-invalid={indexTerritoire === 0 && erreursVisibles.communes ? "true" : undefined}
                      aria-describedby={indexTerritoire === 0 && erreursVisibles.communes ? "erreur-communes" : undefined}
                    />
                    <span>Tout {(TERRITOIRES.find((t) => t.id === territoire.id) || { le: territoire.libelle }).le}</span>
                  </label>
                  <div className="saisie-regle__cases">
                    {territoire.communes.map((commune) => {
                      const etat = etatCommune(saisie, commune);
                      if (commune.codesPostaux.length <= 1) {
                        return (
                          <label key={commune.codeInsee} className="case-ligne">
                            <CaseMixte etat={etat} onChange={(e) => basculerAvec(basculerCommune, commune, e.target.checked)} />
                            <span>
                              {commune.nom} <span className="saisie-regle__cp">{commune.codesPostaux.map((cp) => cp.codePostal).join(", ")}</span>
                            </span>
                          </label>
                        );
                      }
                      const retenus = saisie.communes[commune.codeInsee] || [];
                      return (
                        <div key={commune.codeInsee} className="saisie-regle__commune-codes">
                          <label className="case-ligne">
                            <CaseMixte etat={etat} onChange={(e) => basculerAvec(basculerCommune, commune, e.target.checked)} />
                            <span>{commune.nom}</span>
                          </label>
                          <div className="saisie-regle__codes" role="group" aria-label={`Codes postaux de ${commune.nom}`}>
                            {commune.codesPostaux.map((cp) => (
                              <label key={cp.codePostal} className="case-ligne case-ligne--code">
                                <input
                                  type="checkbox"
                                  checked={retenus.includes(cp.codePostal)}
                                  onChange={(e) => basculerAvec((saisieActuelle, c, coche) => basculerCodePostal(saisieActuelle, c, cp.codePostal, coche), commune, e.target.checked)}
                                />
                                <span>
                                  {cp.codePostal}
                                  {cp.lieux.length > 0 && <span className="saisie-regle__cp"> {cp.lieux.map(casseLieu).join(", ")}</span>}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </details>
            );
          })}
          </div>
          <p className="saisie-regle__aide">Cocher une commune retient tous ses codes postaux ; décochez un code pour l'exclure.</p>
          <Erreur id="erreur-communes" message={erreursVisibles.communes} />
        </section>

        <section aria-labelledby="regle-vers">
          <h3 id="regle-vers">Vers qui ?</h3>
          <p className="saisie-regle__vers-qui">
            <i className="fa fa-info-circle" aria-hidden="true" />
            <span>Toujours en accompagnement total, sur le quota FSE+ de l'opérateur, dans le territoire de la commune de l'allocataire.</span>
          </p>
          <fieldset className="saisie-regle__groupe" aria-describedby={erreursVisibles.operateurs ? "erreur-operateurs" : undefined}>
            <legend>
              Opérateurs <span aria-hidden="true">*</span>
            </legend>
            {operateurs.length + horsReferentiel.length === 0 ? (
              <p className="saisie-regle__vide">Aucun opérateur ne propose l'accompagnement total. Le régime se coche sur la fiche de l'opérateur.</p>
            ) : (
              <div className="saisie-regle__operateurs">
                {[...operateurs, ...horsReferentiel].map((o, i) => {
                  const coche = saisie.operateurs.includes(o.id);
                  return (
                    <label key={o.id} className={`choix-carte${coche ? " is-checked" : ""}`}>
                      <input
                        type="checkbox"
                        checked={coche}
                        onChange={(e) => changer("operateurs", basculer(saisie.operateurs, o.id, e.target.checked))}
                        data-champ-regle={i === 0 ? "operateurs" : undefined}
                      />
                      <span className="choix-carte__texte">
                        <span className="choix-carte__nom">
                          {o.nom}
                          {!o.enService && (
                            <Pill tone={o.statut === "INACTIF" ? "ko" : "neutral"} className="ml-2">
                              {o.statut === "EN_PREPARATION" ? "En préparation" : o.statut === "INACTIF" ? "Désactivé" : "Pas en service"}
                            </Pill>
                          )}
                        </span>
                        {o.sigle && <span className="choix-carte__detail">{o.sigle}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="saisie-regle__aide">Cochez-en plusieurs pour répartir les allocataires à parts égales. Un opérateur en préparation, ou dont le quota est atteint, ne reçoit personne.</p>
            <Erreur id="erreur-operateurs" message={erreursVisibles.operateurs} />
          </fieldset>
        </section>

        <div className="saisie-regle__rang">
          <label htmlFor="regle-rang">Position dans la liste</label>
          <select id="regle-rang" className="form-control" value={saisie.rang} onChange={(e) => changer("rang", Number(e.target.value))}>
            {Array.from({ length: nombre }, (_, i) => i + 1).map((rang) => (
              <option key={rang} value={rang}>
                {rang}
              </option>
            ))}
          </select>
          <span>sur {nombre}</span>
        </div>
      </form>
    </ModaleLocale>
  );
};

export default ModaleRegle;
